import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Briefcase,
  CalendarClock,
  CreditCard,
  Download,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  RefreshCw,
  Save,
  Search,
  Settings,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { adminTokenKey, api, downloadCsv, formatCurrency } from "@/lib/api";
import "./AdminDashboard.css";

const TABS = [
  { id: "overview", label: "Overview", Icon: LayoutDashboard },
  { id: "consultations", label: "Consultations", Icon: Users },
  { id: "careers", label: "Careers", Icon: Briefcase },
  { id: "contacts", label: "Contacts", Icon: MessageSquare },
  { id: "payments", label: "Payments", Icon: CreditCard },
  { id: "bookings", label: "Bookings", Icon: CalendarClock },
  { id: "analytics", label: "Analytics", Icon: BarChart3 },
  { id: "settings", label: "Settings", Icon: Settings },
];

const STATUSES = {
  consultations: ["New", "Contacted", "Qualified", "Proposal Sent", "Won", "Lost"],
  careers: ["New", "Shortlisted", "Interview", "Rejected", "Hired"],
  contacts: ["New", "Contacted", "Closed"],
  payments: ["Pending", "Paid", "Refunded"],
  bookings: ["Pending", "Paid", "Scheduled", "Completed", "Cancelled"],
};

const COLUMNS = {
  consultations: [
    { key: "name", label: "Name" },
    { key: "company", label: "Company" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "revenue_range", label: "Revenue" },
    { key: "growth_challenge", label: "Challenge" },
    { key: "created_at", label: "Date", type: "date" },
    { key: "status", label: "Status", type: "status" },
  ],
  careers: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "role", label: "Role" },
    { key: "college", label: "College" },
    { key: "graduation_year", label: "Grad Year" },
    { key: "created_at", label: "Date", type: "date" },
    { key: "status", label: "Status", type: "status" },
  ],
  contacts: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "message", label: "Message" },
    { key: "created_at", label: "Date", type: "date" },
    { key: "status", label: "Status", type: "status" },
  ],
  payments: [
    { key: "customer_name", label: "Customer" },
    { key: "email", label: "Email" },
    { key: "plan_name", label: "Plan" },
    { key: "amount", label: "Amount", type: "currency" },
    { key: "payment_status", label: "Status", type: "status" },
    { key: "created_at", label: "Date", type: "date" },
  ],
  bookings: [
    { key: "customer_name", label: "Customer" },
    { key: "email", label: "Email" },
    { key: "plan_name", label: "Plan" },
    { key: "payment_status", label: "Status", type: "status" },
    { key: "created_at", label: "Date", type: "date" },
    { key: "scheduling_url", label: "Link", type: "link" },
  ],
};

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const fmtCell = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));

function Toolbar({ search, setSearch, status, setStatus, dateRange, setDateRange, dateFrom, setDateFrom, dateTo, setDateTo, statusOptions, onRefresh, onExport, showStatus = true }) {
  return (
    <div className="admin-toolbar">
      <div className="admin-toolbar-row">
        <div className="admin-search-wrap">
          <Search size={15} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, company…" />
        </div>
        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="admin-select">
          <option value="">All dates</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="custom">Custom Range</option>
        </select>
        {showStatus && (
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="admin-select">
            <option value="">All statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <div className="admin-toolbar-actions">
          <button type="button" onClick={onRefresh} className="admin-btn-icon" title="Refresh"><RefreshCw size={15} /></button>
          <button type="button" onClick={onExport} className="admin-btn-icon" title="Export CSV"><Download size={15} /></button>
        </div>
      </div>
      {dateRange === "custom" && (
        <div className="admin-toolbar-row">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="admin-input-date" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="admin-input-date" />
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dateRange, setDateRange] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem(adminTokenKey);
  useEffect(() => {
    if (!token) navigate("/admin/login");
  }, [navigate, token]);

  const dateParams = () => {
    const params = {};
    if (dateRange) params.date_range = dateRange;
    if (dateRange === "custom" && dateFrom) {
      params.date_from = new Date(`${dateFrom}T00:00:00`).toISOString();
      if (dateTo) params.date_to = new Date(`${dateTo}T23:59:59`).toISOString();
    }
    return params;
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const filters = dateParams();
      if (active === "overview") {
        setOverview((await api.get("/admin/overview", { params: filters })).data);
      } else if (active === "analytics") {
        setAnalytics((await api.get("/admin/analytics", { params: filters })).data);
      } else if (active === "settings") {
        setSettings((await api.get("/admin/settings")).data);
      } else {
        setRows((await api.get(`/admin/${active}`, { params: { search, status, ...filters } })).data);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(adminTokenKey);
        navigate("/admin/login");
      } else {
        toast.error("Could not load dashboard data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = ["overview", "analytics", "settings"].includes(active) ? 0 : 250;
    const timer = setTimeout(load, delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, status, search, dateRange, dateFrom, dateTo]);

  const resetFilters = () => {
    setSearch("");
    setStatus("");
    setDateRange("");
    setDateFrom("");
    setDateTo("");
  };

  const updateRecord = async (id, patch) => {
    try {
      await api.patch(`/admin/${active}/${id}`, patch);
      toast.success("Updated.");
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem(adminTokenKey);
    navigate("/admin/login");
  };

  const tableRows = useMemo(() => rows, [rows]);

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="admin-header-kicker">Operations</p>
          <h1 className="admin-header-title">House of Projects</h1>
        </div>
        <button type="button" onClick={logout} className="admin-btn-icon" title="Log out"><LogOut size={16} /></button>
      </header>

      <div className="admin-layout">
        <aside className="admin-sidebar">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => { setActive(id); resetFilters(); }}
              className={`admin-nav-item${active === id ? " active" : ""}`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </aside>

        <main className="admin-main">
          {loading && <p className="admin-loading">Loading…</p>}

          {active === "overview" && overview && (
            <OverviewPanel data={overview} />
          )}

          {active === "analytics" && analytics && (
            <>
              <Toolbar
                search={search}
                setSearch={setSearch}
                status={status}
                setStatus={setStatus}
                dateRange={dateRange}
                setDateRange={setDateRange}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                statusOptions={[]}
                onRefresh={load}
                onExport={() => downloadCsv("analytics.csv", analytics.recent_events || [])}
                showStatus={false}
              />
              <AnalyticsPanel data={analytics} />
            </>
          )}

          {active === "settings" && settings && (
            <SettingsPanel settings={settings} setSettings={setSettings} />
          )}

          {["consultations", "careers", "contacts", "payments", "bookings"].includes(active) && (
            <>
              <Toolbar
                search={search}
                setSearch={setSearch}
                status={status}
                setStatus={setStatus}
                dateRange={dateRange}
                setDateRange={setDateRange}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                statusOptions={STATUSES[active] || []}
                onRefresh={load}
                onExport={() => downloadCsv(`${active}.csv`, tableRows)}
              />
              <RecordsTable
                type={active}
                rows={tableRows}
                columns={COLUMNS[active]}
                statuses={STATUSES[active]}
                onUpdate={updateRecord}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function OverviewPanel({ data }) {
  const cards = [
    { label: "Total Leads", value: data.total_leads },
    { label: "Consultations", value: data.consultation_requests },
    { label: "Career Applications", value: data.career_applications },
    { label: "Contacts", value: data.contact_requests },
    { label: "Bookings", value: data.bookings_scheduled },
    { label: "Payments Received", value: data.payments_received },
    { label: "Analytics Events", value: data.analytics_events ?? 0 },
  ];
  return (
    <div>
      <div className="admin-kpi-grid">
        {cards.map(({ label, value }) => (
          <div key={label} className="admin-kpi">
            <span className="admin-kpi-label">{label}</span>
            <span className="admin-kpi-value">{value ?? 0}</span>
          </div>
        ))}
      </div>
      <div className="admin-panel">
        <h2 className="admin-panel-title">Recent Activity</h2>
        <div className="admin-activity-list">
          {(data.recent_activity || []).map((item) => (
            <div key={item.id} className="admin-activity-row">
              <div>
                <strong>{item.title}</strong>
                <span className="admin-muted">{item.activity_type}{item.actor ? ` · ${item.actor}` : ""}</span>
              </div>
              <span className="admin-muted">{fmtDate(item.created_at)}</span>
            </div>
          ))}
          {!data.recent_activity?.length && <p className="admin-empty">No activity yet.</p>}
        </div>
      </div>
    </div>
  );
}

function RecordsTable({ type, rows, columns, statuses, onUpdate }) {
  const statusField = type === "payments" || type === "bookings" ? "payment_status" : "status";
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((col) => {
                const val = row[col.key] ?? (col.key === "created_at" ? row.submitted_at : undefined);
                if (col.type === "status" && statuses) {
                  return (
                    <td key={col.key}>
                      <select
                        value={row[statusField] || ""}
                        onChange={(e) => onUpdate(row.id, { status: e.target.value })}
                        className="admin-status-select"
                      >
                        {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  );
                }
                if (col.type === "date") return <td key={col.key}>{fmtDate(val)}</td>;
                if (col.type === "currency") return <td key={col.key}>{formatCurrency(val, row.currency)}</td>;
                if (col.type === "link") {
                  return (
                    <td key={col.key}>
                      {val ? <a href={val} target="_blank" rel="noreferrer" className="admin-link">Open</a> : "-"}
                    </td>
                  );
                }
                return <td key={col.key} className={col.key === "message" || col.key === "growth_challenge" ? "admin-cell-wrap" : ""}>{fmtCell(val)}</td>;
              })}
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={columns.length} className="admin-empty">No records found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AnalyticsPanel({ data }) {
  const metrics = [
    { label: "Page Views", value: data.page_views ?? data.event_counts?.page_view ?? 0 },
    { label: "CTA Clicks", value: data.cta_clicks ?? data.event_counts?.cta_click ?? 0 },
    { label: "Consultation Starts", value: data.consultation_starts ?? data.event_counts?.consultation_start ?? 0 },
    { label: "Consultation Submissions", value: data.consultation_submissions ?? data.event_counts?.consultation_submitted ?? 0 },
    { label: "Career Submissions", value: data.career_submissions ?? data.event_counts?.career_submitted ?? 0 },
    { label: "Contact Submissions", value: data.contact_submissions ?? data.event_counts?.contact_submitted ?? 0 },
  ];
  return (
    <div>
      <div className="admin-kpi-grid admin-kpi-grid-3">
        {metrics.map(({ label, value }) => (
          <div key={label} className="admin-kpi">
            <span className="admin-kpi-label">{label}</span>
            <span className="admin-kpi-value">{value}</span>
          </div>
        ))}
      </div>
      <div className="admin-split-grid">
        <InsightTable title="Top Pages" rows={data.most_visited_pages} />
        <InsightTable title="Top CTAs" rows={data.top_ctas} />
        <InsightTable title="Devices" rows={data.device_mix} />
      </div>
      <div className="admin-panel">
        <h2 className="admin-panel-title">Event Log</h2>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Event</th><th>Page / Label</th><th>Device</th><th>Time</th></tr></thead>
            <tbody>
              {(data.recent_events || []).map((ev) => (
                <tr key={ev.id}>
                  <td>{ev.event_type}</td>
                  <td>{ev.label || ev.page || ev.path || "-"}</td>
                  <td>{ev.device_type || "-"}</td>
                  <td>{fmtDate(ev.created_at)}</td>
                </tr>
              ))}
              {!data.recent_events?.length && (
                <tr><td colSpan={4} className="admin-empty">No events yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InsightTable({ title, rows }) {
  return (
    <div className="admin-panel">
      <h3 className="admin-panel-title">{title}</h3>
      <table className="admin-table admin-table-compact">
        <tbody>
          {(rows || []).map((row) => (
            <tr key={row._id}>
              <td>{row._id || "Unknown"}</td>
              <td className="admin-num">{row.count}</td>
            </tr>
          ))}
          {!rows?.length && <tr><td colSpan={2} className="admin-empty">No data</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SettingsPanel({ settings, setSettings }) {
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings/plans", { plans: settings.plans });
      setSettings((s) => ({ ...s, plans: data.plans }));
      toast.success("Plans saved.");
    } catch {
      toast.error("Could not save plans.");
    }
  };
  const update = (idx, key, value) => {
    setSettings((s) => ({
      ...s,
      plans: s.plans.map((p, i) => (i === idx ? { ...p, [key]: key === "price" ? Math.round(Number(value) * 100) : value } : p)),
    }));
  };
  return (
    <div className="admin-panel">
      <h2 className="admin-panel-title">Consultation Plans</h2>
      <div className="admin-settings-grid">
        {settings.plans.map((plan, idx) => (
          <div key={plan.id} className="admin-settings-row">
            <input value={plan.name} onChange={(e) => update(idx, "name", e.target.value)} className="admin-input" />
            <input type="number" value={(plan.price || 0) / 100} onChange={(e) => update(idx, "price", e.target.value)} className="admin-input" />
            <input value={plan.duration} onChange={(e) => update(idx, "duration", e.target.value)} className="admin-input" />
            <select value={plan.active ? "true" : "false"} onChange={(e) => update(idx, "active", e.target.value === "true")} className="admin-select">
              <option value="true">Active</option>
              <option value="false">Hidden</option>
            </select>
          </div>
        ))}
      </div>
      <button type="button" onClick={save} className="admin-btn-primary"><Save size={15} /> Save Plans</button>
      <div className="admin-kpi-grid admin-kpi-grid-2" style={{ marginTop: "1rem" }}>
        <div className="admin-kpi"><span className="admin-kpi-label">Razorpay</span><span className="admin-kpi-value">{settings.razorpay_configured ? "Yes" : "No"}</span></div>
        <div className="admin-kpi"><span className="admin-kpi-label">Scheduling</span><a href={settings.scheduling_url} target="_blank" rel="noreferrer" className="admin-link">Calendar</a></div>
      </div>
    </div>
  );
}
